import { Alert, Box, Chip, LinearProgress, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, IntlShape, MessageDescriptor, PrimitiveType, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import z from 'zod';
import {
    bulkActivityClose,
    bulkActivityInterrupt,
    bulkActivityResume,
    bulkActivityTag,
    getActivitiesPermissions,
    getActivitiesWithPermission,
} from '../../api/bulk-action';
import { ActivityBulkDTO, DocumentType, TagReadDTO } from '../../api/dto';
import { ImpactLink } from '../../components/ImpactLink';
import { PersonTooltip } from '../../components/PersonTooltip';
import { DocumentResultCard } from '../../components/searching/DocumentResultCard';
import { FilterOperatorEnum, SearchFilter } from '../../components/searching/filters';
import { SearchPage } from '../../components/searching/SearchPage';
import { StatusChip } from '../../components/StatusChip';
import { DATATYPES } from '../../components/tables/table/utils';
import { defaultUrlParamsSchema, withUrlParams } from '../../components/wrappers/withUrlParams';
import { FormGenerator } from '../../forms/FormGenerator';
import { tagSchema } from '../../forms/schemas/tag';
import { StatusColor } from '../../lib/colors/StatusColor';
import { EntityType } from '../../lib/information/types/EntityType';
import { getLovLabel } from '../../lib/label';
import { translateValue } from '../../lib/language';
import { getActivityStatus } from '../../lib/status';
import { getRoute, ROUTES } from '../../routes/routes';
import { ActivityBulkActions } from './ActivitySearchPage';

const columnHelper = createColumnHelper<ActivityBulkDTO>();

const getColumns = (intl: IntlShape) => [
    columnHelper.accessor((row) => row.id ?? '', {
        id: 'id',
        header: intl.formatMessage({ id: 'activity.name' }),
        size: 60,
        cell: ({ getValue, row: { original } }) => (
            <ImpactLink
                to={getRoute({
                    path: ROUTES.activity.view,
                    params: { id: (original as any).id },
                })}
            >
                {getValue()}
            </ImpactLink>
        ),
    }),
    columnHelper.accessor((row) => getLovLabel(translateValue(row.status, intl.locale)) ?? '', {
        id: 'status.label',
        header: intl.formatMessage({ id: 'document.field.status' }),
        size: 120,
        cell: ({ row }) => <StatusChip {...getActivityStatus(row.original, intl)} size="small" />,
        meta: {
            dataType: DATATYPES.ENUMERATION,
        },
    }),
    columnHelper.accessor((row) => row.id ?? '', {
        id: 'bulkActionPermission',
        header: intl.formatMessage({ id: 'bulk.action.permission' }),
        size: 80,
        cell: ({ row: { original } }) => {
            const available = original.bulkActionPermission;
            return (
                <Chip
                    size="small"
                    label={intl.formatMessage({
                        id: available ? 'bulk.action.permission.available' : 'bulk.action.permission.unavailable',
                    })}
                    sx={{
                        backgroundColor: available ? StatusColor.SUCCESS : StatusColor.ERROR,
                        color: available ? 'inherit' : 'white',
                    }}
                />
            );
        },
        meta: {
            dataType: DATATYPES.ENUMERATION,
        },
    }),
    columnHelper.accessor((row) => row.activityResponsible?.orgUnit?.code ?? '', {
        id: 'activityResponsible.orgUnit.code',
        header: intl.formatMessage({ id: 'document.field.responsibleGroup' }),
        size: 135,
    }),
    columnHelper.accessor(
        (row) => (row.facility?.description ? getLovLabel(translateValue(row.facility, intl.locale)) : ''),
        {
            id: 'facility.code',
            header: intl.formatMessage({ id: 'document.field.facility' }),
            size: 120,
            meta: {
                dataType: DATATYPES.ENUMERATION,
            },
        }
    ),
    columnHelper.accessor((row) => (row.type ? getLovLabel(translateValue(row.type, intl.locale)) : ''), {
        id: 'type.code',
        header: intl.formatMessage({ id: 'document.field.type' }),
        size: 120,
    }),
    columnHelper.accessor((row) => row.system?.name ?? '', {
        id: 'system.name',
        header: intl.formatMessage({ id: 'activity.field.system' }),
        size: 110,
    }),
    columnHelper.accessor((row) => row.title ?? '', {
        id: 'title',
        header: intl.formatMessage({ id: 'document.field.title' }),
        size: 260,
    }),
    columnHelper.accessor((row) => row.activityResponsible?.searchLabel, {
        id: 'activityResponsible.searchLabel',
        header: intl.formatMessage({ id: 'document.field.responsible' }),
        size: 200,
        cell: ({ row: { original }, getValue }) =>
            !!original.activityResponsible && (
                <PersonTooltip person={original.activityResponsible}>
                    <Typography variant="inherit">{getValue()}</Typography>
                </PersonTooltip>
            ),
    }),
    columnHelper.accessor((row) => row.activityResponsible?.phone1, {
        id: 'phone',
        header: `${intl.formatMessage({ id: 'document.person.phone' })} / ${intl.formatMessage({
            id: 'document.person.mobile',
        })}`,
        size: 200,
        enableSorting: false, // Not sure about that one
        cell: ({ row: { original }, getValue }) =>
            !!original.activityResponsible?.phone1 && !!original.activityResponsible?.phone3Display ? (
                <Typography variant="inherit">
                    {getValue()} / {original.activityResponsible.phone3Display}
                </Typography>
            ) : (
                <Typography variant="inherit">{getValue()}</Typography>
            ),
    }),
];

const schema = defaultUrlParamsSchema.extend({
    pathParams: z.object({
        action: z.nativeEnum(ActivityBulkActions),
    }),
});

const BULK_ACTION_API_CALLS = {
    [ActivityBulkActions.RESUME]: bulkActivityResume,
    [ActivityBulkActions.INTERRUPT]: bulkActivityInterrupt,
    [ActivityBulkActions.CLOSE]: bulkActivityClose,
    [ActivityBulkActions.TAG]: bulkActivityTag,
};

interface CountChipProps {
    messageId: MessageDescriptor['id'];
    value: Record<string, PrimitiveType>;
    background: string;
    color?: string;
}

interface TagBulkActionFormValues {
    tag: TagReadDTO | null;
}

export const ActivityBulkActionPageContent: React.FC<z.infer<typeof schema>> = ({ pathParams: { action } }) => {
    const intl = useIntl();

    const navigate = useNavigate();

    const location = useLocation();
    const selectedIds = (location.state?.selectedIds ?? []) as number[];

    const [selectedRows, setSelectedRows] = useState<ActivityBulkDTO[]>(() =>
        selectedIds.map((id) => ({ id } as ActivityBulkDTO))
    );

    const tagForm = useForm<TagBulkActionFormValues>({
        mode: 'onChange',
        defaultValues: { tag: null },
    });
    const selectedTag = useWatch({ control: tagForm.control, name: 'tag' });

    const baseFilters: SearchFilter[] = selectedIds.length
        ? [{ field: 'id', operator: FilterOperatorEnum.IS_ANY_OF, value: selectedIds }]
        : [];

    const bulkActionType = action.toUpperCase();

    const {
        isError: isActivitiesPermissionsError,
        isLoading: isLoadingActivitiesPermissions,
        data: activitiesPermissions,
    } = useQuery(['activitiesPermissions', action, selectedIds], () => {
        return getActivitiesPermissions({ bulkActionType, ids: selectedIds });
    });

    const permittedIds = useMemo(
        () => new Set(activitiesPermissions?.filter((p) => p.permission).map((p) => p.id)),
        [activitiesPermissions]
    );

    const availableCount = useMemo(
        () => selectedRows.filter((s) => permittedIds.has(s.id)).length,
        [selectedRows, permittedIds]
    );

    const unavailableCount = selectedRows.length - availableCount;

    const { mutate: confirmBulkAction, isLoading: isConfirming } = useMutation(
        (params: { ids: number[]; tagName?: string }) =>
            action === ActivityBulkActions.TAG
                ? BULK_ACTION_API_CALLS[action]({ ids: params.ids, tagName: params.tagName ?? '' })
                : BULK_ACTION_API_CALLS[action]({ ids: params.ids }),
        {
            onSuccess: () => {
                toast.success(<strong>{intl.formatMessage({ id: 'bulk.action.submitted' })}</strong>, {
                    autoClose: 20000,
                });
                navigate(
                    getRoute({
                        path: ROUTES.activity.search,
                    }),
                    {
                        state: {
                            ...location.state,
                            page: 1,
                            selectedIds: (selectedRows ?? []).map((row) => row.id),
                            showBulkAction: true,
                        },
                    }
                );
            },
        }
    );

    const handleConfirm = (rows: ActivityBulkDTO[]) => {
        const ids = rows.filter((row) => permittedIds.has(row.id)).map((row) => row.id);
        confirmBulkAction({ ids, tagName: selectedTag?.name });
    };

    const isTagAction = action === ActivityBulkActions.TAG;
    const tagFormSchema = useMemo(
        () =>
            tagSchema({
                path: 'tag',
                label: intl.formatMessage({ id: 'activity.field.tags' }),
                required: true,
                extraProps: {},
            }),
        [intl]
    );

    const CountChip: React.FC<CountChipProps> = ({ messageId, value, background, color = 'white' }) => (
        <Chip
            size="small"
            label={intl.formatMessage({ id: messageId }, value)}
            sx={{ backgroundColor: background, color }}
        />
    );

    if (isLoadingActivitiesPermissions) {
        return <LinearProgress />;
    } else if (isActivitiesPermissionsError) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <FormattedMessage id="common.error" />
            </Alert>
        );
    }

    return (
        <>
            <SearchPage
                title={intl.formatMessage({ id: 'title.activityBulkActions' })}
                columns={getColumns(intl)}
                pageKey={'ActivityBulkActionPage'}
                documentType={DocumentType.ACTIVITY}
                filterSchemas={[]}
                baseFilters={baseFilters}
                pagedApiCallWithParam={{
                    method: getActivitiesWithPermission,
                    param: {
                        bulkActionType,
                    },
                }}
                CardItem={({ item, handleExpandToggle, isExpanded }) => (
                    <DocumentResultCard
                        key={item.id}
                        document={{ type: EntityType.ActivityNim, activity: item }}
                        isExpanded={isExpanded(item)}
                        onExpandToggle={() => handleExpandToggle(item)}
                    />
                )}
                initialSort={{ id: 'id', desc: true }}
                selectable={true}
                embedded
                selectedRows={selectedRows}
                onSelect={setSelectedRows}
                getRowId={(row) => row.id}
                header={
                    <Box display="flex" flexDirection="column">
                        <Box alignItems="center" justifyContent="space-between" display="flex">
                            <Box>
                                <Typography variant="caption" textTransform="uppercase">
                                    <FormattedMessage id="bulk.action" />
                                </Typography>
                                <Typography variant="h6">
                                    <FormattedMessage id={`document.action.${action}`} />
                                </Typography>
                            </Box>
                            <Box alignItems="center" display="flex" gap={2}>
                                <CountChip
                                    messageId="bulk.action.selected"
                                    value={{ selected: selectedRows.length, total: selectedIds.length }}
                                    background={StatusColor.PRIMARY}
                                />
                                <CountChip
                                    messageId="bulk.action.available"
                                    value={{ number: availableCount }}
                                    background={StatusColor.SUCCESS}
                                    color="inherit"
                                />
                                <CountChip
                                    messageId="bulk.action.unavailable"
                                    value={{ number: unavailableCount }}
                                    background={StatusColor.ERROR}
                                />
                            </Box>
                        </Box>
                        {isTagAction ? (
                            <FormProvider {...tagForm}>
                                <Box mt={2} sx={{ maxWidth: 420 }}>
                                    <FormGenerator rootElement={tagFormSchema} form={tagForm} />
                                </Box>
                            </FormProvider>
                        ) : null}
                        {action === ActivityBulkActions.CLOSE && availableCount > 0 ? (
                            <Box my={1}>
                                <Alert severity="warning">
                                    <FormattedMessage
                                        id="bulk.action.warningClose"
                                        values={{
                                            number: availableCount,
                                        }}
                                    />
                                </Alert>
                            </Box>
                        ) : null}
                    </Box>
                }
                hideDefaultHeader={true}
                bulkActionsConfirmation={{
                    action,
                    selected: availableCount,
                    total: selectedRows.length,
                    searchPage: ROUTES.activity.search,
                    onConfirm: handleConfirm,
                    disabled: isConfirming || availableCount === 0 || (isTagAction && !selectedTag),
                }}
            />
        </>
    );
};

export const ActivityBulkActionPage = withUrlParams(ActivityBulkActionPageContent, schema);
